import { yupResolver } from '@hookform/resolvers/yup'
import { useCallback, useEffect, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router'

import { api } from '@/apiClient/api'
import { isErrorAPIError } from '@/apiClient/helpers'
import type {
  ActivityNotOpenToPublic,
  ActivityOpenToPublic,
  ProUserCreationBodyV2Model,
  SignupSimulationPayload,
  TargetAudience,
} from '@/apiClient/v1'
import { useAnalytics } from '@/app/App/analytics/firebase'
import { Events } from '@/commons/core/FirebaseEvents/constants'
import {
  RECAPTCHA_ERROR,
  RECAPTCHA_ERROR_MESSAGE,
} from '@/commons/core/shared/constants'
import { useInitReCaptcha } from '@/commons/hooks/useInitReCaptcha'
import { useLogEventOnUnload } from '@/commons/hooks/useLogEventOnUnload'
import { useSnackBar } from '@/commons/hooks/useSnackBar'
import { getReCaptchaToken } from '@/commons/utils/recaptcha'

import { SIGNUP_FORM_DEFAULT_VALUES } from './constants'
import styles from './SignupContainer.module.scss'
import { SignupForm } from './SignupForm'
import { validationSchema } from './validationSchema'

function extractStructureInfoFromParams(
  searchParams: URLSearchParams
): SignupSimulationPayload | null {
  const siret = searchParams.get('siret')
  if (siret !== null) {
    return {
      activity: searchParams.get('activity') as
        | ActivityOpenToPublic
        | ActivityNotOpenToPublic,
      isOpenToPublic: searchParams.get('isOpenToPublic') === 'true',
      siret,
      targets: searchParams.getAll('targets') as TargetAudience[],
    }
  }

  return null
}

export const SignupContainer = (): JSX.Element => {
  const navigate = useNavigate()
  const snackBar = useSnackBar()
  const { logEvent } = useAnalytics()
  const [searchParams] = useSearchParams()

  useInitReCaptcha()

  const hookForm = useForm<ProUserCreationBodyV2Model>({
    defaultValues: SIGNUP_FORM_DEFAULT_VALUES,
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
  })

  const {
    handleSubmit,
    formState: { errors, touchedFields },
    setError,
    getValues,
  } = hookForm

  const onSubmit = async (values: ProUserCreationBodyV2Model) => {
    try {
      /* istanbul ignore next : ENV dependant */
      const token = await getReCaptchaToken('signup')
      const structureSimulationInfos =
        extractStructureInfoFromParams(searchParams)
      await api.signupPro({
        body: {
          ...values,
          token, // set token at form submission
          structureSimulationInfos,
        },
      })
      onHandleSuccess()
    } catch (error) {
      if (error === RECAPTCHA_ERROR) {
        snackBar.error(RECAPTCHA_ERROR_MESSAGE)
      } else {
        const body = isErrorAPIError(error) ? error.body : {}
        onHandleFail(body)
      }
    }
  }

  const onHandleSuccess = () => {
    logEvent(Events.SIGNUP_FORM_SUCCESS, {})
    void navigate('/inscription/compte/confirmation', {
      replace: true,
      state: { email: getValues('email') },
    })
  }

  const onHandleFail = (errors: Partial<ProUserCreationBodyV2Model>) => {
    for (const [field, message] of Object.entries(errors)) {
      setError(field as keyof ProUserCreationBodyV2Model, {
        type: 'manual',
        message: message as string,
      })
    }

    snackBar.error(
      'Une ou plusieurs erreurs sont présentes dans le formulaire.'
    )
  }

  // Track the state of the form when the user gives up
  const touchedRef = useRef(touchedFields)
  const errorsRef = useRef(errors)

  useEffect(() => {
    touchedRef.current = touchedFields
    errorsRef.current = errors
  }, [touchedFields, errors])

  const logFormAbort = useCallback(() => {
    const filledFields = Object.keys(touchedRef.current)
    if (filledFields.length === 0) {
      return
    }

    const filledWithErrors = Object.keys(
      Object.fromEntries(
        Object.entries(errorsRef.current).filter(([errorKey]) =>
          filledFields.includes(errorKey)
        )
      )
    )
    return logEvent(Events.SIGNUP_FORM_ABORT, {
      filled: filledFields,
      filledWithErrors: filledWithErrors,
    })
  }, [logEvent])

  // Track the form state on tab closing
  useLogEventOnUnload(() => logFormAbort())

  // Track the form state on component unmount
  useEffect(() => {
    return () => {
      if (Object.keys(errorsRef.current).length === 0) {
        return
      }
      logFormAbort()
    }
  }, [logFormAbort])

  return (
    <div className={styles['validation-container']}>
      <section className={styles['content']}>
        <FormProvider {...hookForm}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <SignupForm />
          </form>
        </FormProvider>
      </section>
    </div>
  )
}
